/**
 * Locale-aware value formatting (§25).
 *
 * Everything here delegates to `Intl`, which every target runtime ships with
 * full ICU data. Hand-rolled formatting is how a product ends up showing
 * `07/08/2026` to a reader who parses it as 8 July and to another who parses it
 * as 7 August.
 */
import { type LocaleCode, localeInfo } from './locales.js';

/** IEC binary units. `Intl` localises the unit names; the maths stays ours. */
const SIZE_UNITS = ['byte', 'kilobyte', 'megabyte', 'gigabyte', 'terabyte'] as const;

export function formatNumber(locale: LocaleCode, value: number, opts?: Intl.NumberFormatOptions): string {
  return new Intl.NumberFormat(localeInfo(locale).intlTag, opts).format(value);
}

export function formatCurrency(locale: LocaleCode, value: number, currency: string): string {
  return new Intl.NumberFormat(localeInfo(locale).intlTag, { style: 'currency', currency }).format(value);
}

export function formatPercent(locale: LocaleCode, ratio: number, fractionDigits = 0): string {
  return new Intl.NumberFormat(localeInfo(locale).intlTag, {
    style: 'percent',
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  }).format(ratio);
}

/**
 * File size with a localised unit name.
 *
 * Uses 1024-based steps because that is what every file manager on every target
 * platform shows; presenting 1000-based values would make Modu Viewer disagree
 * with Explorer and Finder about the same file.
 */
export function formatFileSize(locale: LocaleCode, bytes: number): string {
  const tag = localeInfo(locale).intlTag;
  if (!Number.isFinite(bytes) || bytes < 0) return new Intl.NumberFormat(tag).format(0);

  let value = bytes;
  let unitIndex = 0;
  while (value >= 1024 && unitIndex < SIZE_UNITS.length - 1) {
    value /= 1024;
    unitIndex++;
  }

  const unit = SIZE_UNITS[unitIndex]!;
  const fractionDigits = unitIndex === 0 ? 0 : value < 10 ? 1 : 0;
  try {
    return new Intl.NumberFormat(tag, {
      style: 'unit', unit, unitDisplay: 'short',
      minimumFractionDigits: fractionDigits, maximumFractionDigits: fractionDigits,
    }).format(value);
  } catch {
    // A runtime without the `unit` style still gets a correct number.
    const n = new Intl.NumberFormat(tag, {
      minimumFractionDigits: fractionDigits, maximumFractionDigits: fractionDigits,
    }).format(value);
    return `${n} ${['B', 'KB', 'MB', 'GB', 'TB'][unitIndex]}`;
  }
}

export type DateStyle = 'short' | 'medium' | 'long';

export function formatDateTime(locale: LocaleCode, date: Date, style: DateStyle = 'medium'): string {
  return new Intl.DateTimeFormat(localeInfo(locale).intlTag, {
    dateStyle: style, timeStyle: style === 'long' ? 'medium' : 'short',
  }).format(date);
}

export function formatDate(locale: LocaleCode, date: Date, style: DateStyle = 'medium'): string {
  return new Intl.DateTimeFormat(localeInfo(locale).intlTag, { dateStyle: style }).format(date);
}

const RELATIVE_STEPS: readonly (readonly [limit: number, unit: Intl.RelativeTimeFormatUnit, per: number])[] = [
  [60_000, 'second', 1000],
  [3_600_000, 'minute', 60_000],
  [86_400_000, 'hour', 3_600_000],
  [604_800_000, 'day', 86_400_000],
  [2_629_800_000, 'week', 604_800_000],
  [31_557_600_000, 'month', 2_629_800_000],
  [Number.POSITIVE_INFINITY, 'year', 31_557_600_000],
];

/**
 * "3분 전", "3 minutes ago" — used for auto-save timestamps and recovery
 * prompts (§23), where an absolute time reads as noise.
 */
export function formatRelativeTime(locale: LocaleCode, from: Date, now: Date): string {
  const rtf = new Intl.RelativeTimeFormat(localeInfo(locale).intlTag, { numeric: 'auto' });
  const deltaMs = from.getTime() - now.getTime();
  const magnitude = Math.abs(deltaMs);
  for (const [limit, unit, per] of RELATIVE_STEPS) {
    if (magnitude < limit) return rtf.format(Math.round(deltaMs / per), unit);
  }
  return rtf.format(Math.round(deltaMs / 31_557_600_000), 'year');
}

/** Join a list the way the locale does ("A, B and C" / "A、B、C"). */
export function formatList(locale: LocaleCode, items: readonly string[]): string {
  try {
    return new Intl.ListFormat(localeInfo(locale).intlTag, { style: 'long', type: 'conjunction' })
      .format(items);
  } catch {
    return items.join(', ');
  }
}
